import { useEffect, useState } from "react";
import { Form, Input, InputNumber, Select, Switch, Table } from "antd";
import { getFeatureDisplayName } from "../../utils/aiFeatureMetadata";

const { Option } = Select;
const { TextArea } = Input;

const CURRENCY_OPTIONS = [
    { value: "SAR", label: "SAR — Saudi Riyal" },
    { value: "USD", label: "USD — US Dollar" },
    { value: "EUR", label: "EUR — Euro" },
    { value: "GBP", label: "GBP — British Pound" },
    { value: "AED", label: "AED — UAE Dirham" },
    { value: "KWD", label: "KWD — Kuwaiti Dinar" },
    { value: "BHD", label: "BHD — Bahraini Dinar" },
    { value: "OMR", label: "OMR — Omani Rial" },
    { value: "QAR", label: "QAR — Qatari Riyal" },
];

const MODELS_BY_PROVIDER = {
    openai: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"],
    anthropic: ["claude-3-5-sonnet-20241022", "claude-3-haiku-20240307"],
    gemini: ["gemini-2.0-flash", "gemini-2.5-flash"],
    groq: ["llama-3.3-70b-versatile"],
    openrouter: ["openrouter/auto", "deepseek/deepseek-r1-0528:free"],
};

const PlanFormFields = ({ form, metadata }) => {
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const featureOptions = metadata?.features || [];
    const providerOptions = metadata?.providers || [];

    const featuresValue = Form.useWatch("features", form);

    useEffect(() => {
        setSelectedFeatures(featuresValue || []);
    }, [featuresValue]);

    const overrideColumns = [
        {
            title: "Feature",
            dataIndex: "feature_name",
            key: "feature_name",
            render: (featureName) => getFeatureDisplayName(featureName),
        },
        {
            title: "Provider (optional)",
            key: "provider",
            render: (_, record) => (
                <Form.Item
                    name={["feature_overrides_map", record.feature_name, "provider"]}
                    style={{ margin: 0 }}
                >
                    <Select allowClear placeholder="Use global config" style={{ width: 160 }}>
                        {providerOptions.map((p) => (
                            <Option key={p.value} value={p.value}>
                                {p.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            ),
        },
        {
            title: "Model (optional)",
            key: "model_version",
            render: (_, record) => (
                <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                        prev?.feature_overrides_map?.[record.feature_name]?.provider !==
                        cur?.feature_overrides_map?.[record.feature_name]?.provider
                    }
                >
                    {() => {
                        const provider = form.getFieldValue([
                            "feature_overrides_map",
                            record.feature_name,
                            "provider",
                        ]);
                        const models = MODELS_BY_PROVIDER[provider] || [];
                        return (
                            <Form.Item
                                name={["feature_overrides_map", record.feature_name, "model_version"]}
                                style={{ margin: 0 }}
                            >
                                <Select
                                    allowClear
                                    placeholder="Select model"
                                    style={{ width: 200 }}
                                    disabled={!provider}
                                >
                                    {models.map((m) => (
                                        <Option key={m} value={m}>
                                            {m}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            ),
        },
    ];

    return (
        <>
            <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: "Plan name is required" }]}
            >
                <Input placeholder="e.g. Pro" />
            </Form.Item>
            <Form.Item
                name="slug"
                label="Slug"
                rules={[
                    { required: true, message: "Slug is required" },
                    { pattern: /^[a-z0-9_-]+$/, message: "Lowercase letters, numbers, - and _ only" },
                ]}
            >
                <Input placeholder="e.g. pro" />
            </Form.Item>
            <Form.Item name="description" label="Description">
                <TextArea rows={2} placeholder="Plan description" />
            </Form.Item>
            <Form.Item name="amount" label="Price" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="0 for free" />
            </Form.Item>
            <Form.Item name="currency" label="Currency" initialValue="SAR">
                <Select showSearch optionFilterProp="children" placeholder="Select currency">
                    {CURRENCY_OPTIONS.map((c) => (
                        <Option key={c.value} value={c.value}>
                            {c.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name="general_quota_limit"
                label="General Quota Limit (paid LLM requests/month)"
                rules={[{ required: true }]}
            >
                <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
                name="polars_summary_quota_limit"
                label="Polars AI Summary Limit (free tier)"
                tooltip="Max Polars-generated dashboard summaries per month on free plans. Paid plans have unlimited Polars summaries."
            >
                <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 100" />
            </Form.Item>
            <Form.Item name="billing_interval" label="Billing Interval" initialValue="month">
                <Select>
                    <Option value="month">Monthly</Option>
                    <Option value="year">Annual</Option>
                </Select>
            </Form.Item>
            <Form.Item
                name="features"
                label="Enabled AI Features"
                rules={[{ required: true, message: "Select at least one feature" }]}
            >
                <Select
                    mode="multiple"
                    placeholder="Select features"
                    optionFilterProp="children"
                >
                    {featureOptions.map((f) => (
                        <Option key={f.value} value={f.value}>
                            {getFeatureDisplayName(f.value)}
                        </Option>
                    ))}
                </Select>
            </Form.Item>
            {selectedFeatures.length > 0 && (
                <>
                    <p style={{ marginBottom: 8 }}>Optional per-feature provider/model overrides</p>
                    <Table
                        size="small"
                        pagination={false}
                        columns={overrideColumns}
                        dataSource={selectedFeatures.map((f) => ({ feature_name: f, key: f }))}
                    />
                </>
            )}
            <Form.Item
                name="sort_order"
                label="Sort Order"
                initialValue={0}
                tooltip="Display position on pricing and plan pickers (0–4). Lower numbers appear first. Only four slots are shown on the pricing page."
                rules={[
                    { required: true, message: "Sort order is required" },
                    {
                        type: "number",
                        min: 0,
                        max: 4,
                        message: "Sort order must be between 0 and 4",
                    },
                ]}
            >
                <InputNumber min={0} max={4} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="is_default" label="Default Plan" valuePropName="checked">
                <Switch />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
                <Switch />
            </Form.Item>
        </>
    );
};

export const buildPlanPayload = (values) => {
    const overrides = [];
    const map = values.feature_overrides_map || {};
    Object.keys(map).forEach((featureName) => {
        const entry = map[featureName];
        if (entry?.provider && entry?.model_version) {
            overrides.push({
                feature_name: featureName,
                provider: entry.provider,
                model_version: entry.model_version,
            });
        }
    });

    return {
        name: values.name,
        slug: values.slug,
        description: values.description,
        amount: values.amount,
        currency: values.currency || "SAR",
        general_quota_limit: values.general_quota_limit,
        polars_summary_quota_limit: values.polars_summary_quota_limit ?? null,
        quota_period: "month",
        billing_interval: values.billing_interval || "month",
        features: values.features || [],
        feature_overrides: overrides,
        is_default: values.is_default || false,
        active: values.active !== false,
        sort_order: values.sort_order || 0,
    };
};

export const planToFormValues = (plan) => {
    const feature_overrides_map = {};
    (plan.feature_overrides || []).forEach((o) => {
        feature_overrides_map[o.feature_name] = {
            provider: o.provider,
            model_version: o.model_version,
        };
    });
    return {
        name: plan.name,
        slug: plan.slug,
        description: plan.description,
        amount: plan.amount,
        currency: plan.currency,
        general_quota_limit: plan.general_quota_limit,
        polars_summary_quota_limit: plan.polars_summary_quota_limit,
        billing_interval: plan.billing_interval || "month",
        features: plan.features || [],
        feature_overrides_map,
        is_default: plan.is_default,
        active: plan.active,
        sort_order: plan.sort_order,
    };
};

export default PlanFormFields;
